import { getLocaleForTimezone } from '../../../shared/utils/localeUtils.js';

export interface ExportConfig {
  columns: {
    description: boolean;
    project: boolean;
    location: boolean;
    startTime: boolean;
    endTime: boolean;
    duration: boolean;
    day: boolean;
    date: boolean;
    originalTimezone: boolean;
    pause?: boolean; // Only used for day-based export
    rate?: boolean; // Effective hourly rate (billable projects only)
    revenue?: boolean; // Calculated revenue = duration_hours × rate (billable projects only)
  };
  summaries: {
    totalDay: boolean;
    totalWeek: boolean;
    total: boolean;
  };
}

// Export mode type - task-based (default) or day-based
export type ExportMode = "task-based" | "day-based";

// Day-based entry for server-side export
export interface DayBasedExportEntry {
  dateKey: string;
  projectId: string | undefined;
  projectName: string;
  location: string;
  description: string;
  startTime: string;
  endTime: string;
  duration: number;
  pause: number;
  timezone: string | undefined;
  hasOverlap?: boolean; // Whether this day has overlapping entries
  budgetStatus?: "over" | "in" | "under"; // Budget violation status for Excel highlighting
  rate: number | null; // Effective hourly rate (null if mixed rates or non-billable)
  revenue: number | null; // Sum of sub-entry revenues (null if non-billable)
  currency: string | null; // ISO 4217 code (null if non-billable)
}

// Task-based entry for server-side export (pre-processed on client for WYSIWYG consistency)
export interface TaskBasedExportEntry {
  id: string;
  dateKey: string;           // Timezone-aware date (YYYY-MM-DD)
  weekKey: string;           // Timezone-aware week (YYYY-WNN)
  dayName: string;           // Day of week (e.g., "Monday")
  projectId: string | undefined;
  projectName: string;       // Pre-resolved project name
  location: string;
  description: string;
  startTime: string;         // ISO string
  endTime: string | undefined;
  formattedStartTime: string; // Pre-formatted for display (HH:MM)
  formattedEndTime: string;   // Pre-formatted for display (HH:MM or "-")
  duration: number;          // In seconds
  timezone: string | undefined;
  hasOverlap: boolean;       // Whether this entry overlaps with others
  budgetStatus?: "over" | "in" | "under"; // Budget violation status for Excel highlighting
  rate: number | null; // Effective hourly rate (null for non-billable)
  revenue: number | null; // duration_hours × rate (null for non-billable)
  currency: string | null; // ISO 4217 code (null for non-billable)
}

// Display settings for export
export interface ExportDisplaySettings {
  showOverlapIndicator?: boolean;
  showBudgetWarnings?: boolean; // Highlight rows that exceed project budget
  overlapIndicatorColor?: string; // Hex color for overlap row highlighting (e.g. #FEF2F2)
  budgetWarningColor?: string; // Hex color for budget violation row highlighting (e.g. #FEF2F2)
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

// Sort configuration for exports
export interface ExportSortConfig {
  column: string;
  direction: "asc" | "desc";
}

export interface ExportRequest {
  entries: unknown[];
  config: ExportConfig;
  dateRange: DateRange;
  filters: {
    projectId?: string;
    projectIds?: string[];
    location: string;
  };
  exportTimezone: string;
  dateLocale?: string; // BCP 47 locale for date/time formatting (derived from exportTimezone on the page)
  exportLanguage?: 'en' | 'de'; // Language for column headers and day names
  exportMode?: ExportMode; // Default: "task-based"
  taskBasedEntries?: TaskBasedExportEntry[]; // Pre-processed task-based entries (for WYSIWYG consistency)
  dayBasedEntries?: DayBasedExportEntry[]; // Pre-aggregated day-based entries (required when exportMode is "day-based")
  displaySettings?: ExportDisplaySettings; // Display settings for row highlighting
  overlappingEntryIds?: string[]; // Entry IDs that have overlaps (for task-based mode) - deprecated, use taskBasedEntries.hasOverlap
  weekStartsOn?: 0 | 1; // 0 = Sunday, 1 = Monday (default: 1 for ISO 8601)
  sort?: ExportSortConfig; // Sort configuration for Excel output
}

export class ReportsService {
  private getServerUrl(): string {
    return import.meta.env.VITE_API_URL || "";
  }

  async exportReport(request: ExportRequest): Promise<Blob> {
    const serverUrl = this.getServerUrl();
    const url = `${serverUrl}/api/reports/export`;

    // Use the export timezone from the request (user-selected)
    const timezone = request.exportTimezone;

    // Use the pre-computed locale from the page (already handles PRESERVE_ORIGINAL correctly).
    const dateLocale = request.dateLocale ?? getLocaleForTimezone(timezone);

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        "X-Timezone": timezone,
        "X-Date-Locale": dateLocale,
        "X-Export-Language": request.exportLanguage ?? "en",
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Export failed:", errorText);
      throw new Error("Export failed");
    }

    return await response.blob();
  }
}

export const reportsService = new ReportsService();
